import { FC } from 'react';
import { Box, Paper, Skeleton, Stack } from '@mui/material';
import { observer } from 'mobx-react-lite';


/**
 * Header + ProgressFlow stepper skeleton.
 *
 * Rendered by `AssessmentLayout` while the assessment query is in flight,
 * since the header (name + Exit) and stepper both depend on that data.
 */
export const ProgressFlowSkeleton: FC = observer(() => {
  return (

    <Paper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: { xs: 2, sm: 3, md: 4 },
        height: '30%',
        paddingInline: 6,
        paddingTop: 3,
      }}
    >
      {/* Header Skeleton */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        {/* Assessment Name */}
        <Skeleton
          variant="text"
          width={200}
          height={32}
          sx={{ borderRadius: 1 }}
        />
        {/* Exit Button */}
        <Skeleton
          variant="rounded"
          width={80}
          height={36}
          sx={{ borderRadius: 2 }}
        />
      </Box>

      {/* Progress Flow Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        {/* Domain Number (e.g., "Domain 1 of 10") */}
        <Skeleton variant="text" width={120} height={24} />
        {/* Progress Percentage */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
          <Skeleton variant="text" width={60} height={16} />
          <Skeleton variant="text" width={80} height={20} />
        </Box>
      </Box>

      {/* Progress Flow Circles and Lines */}
      <Stack
        direction="row"
        alignItems="center"
        sx={{ width: '100%', mb: 1 }}
      >
        {/* Render 7 circles with connecting lines */}
        {Array.from({ length: 7 }).map((_, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: index === 6 ? 0 : 1,
            }}
          >
            {/* Circle */}
            <Skeleton
              variant="circular"
              width={30}
              height={30}
              sx={{ flexShrink: 0 }}
            />
            {/* Connecting Line (except for last circle) */}
            {index < 6 && (
              <Skeleton
                variant="rounded"
                sx={{
                  height: 6,
                  flex: 1,
                  minWidth: 30,
                  mx: 0.25,
                  borderRadius: 100,
                }}
              />
            )}
          </Box>
        ))}
      </Stack>

      {/* Domain Names Row */}
      <Stack
        direction="row"
        alignItems="flex-start"
        sx={{ width: '100%' }}
      >
        {Array.from({ length: 7 }).map((_, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              flex: index === 6 ? 0 : 1,
            }}
          >
            {/* Domain Name */}
            <Box
              sx={{
                width: 30,
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Skeleton
                variant="text"
                width={60}
                height={16}
                sx={{ transform: 'translateX(-50%)', position: 'relative', left: '50%' }}
              />
            </Box>
            {/* Spacer for line */}
            {index < 6 && <Box sx={{ flex: 1, mx: 0.25 }} />}
          </Box>
        ))}
      </Stack>
    </Paper>
  );
});

/**
 * Domain content + navigation skeleton.
 *
 * Rendered by `AssessmentWorkflowPage` while the per-domain query is in flight.
 * Mirrors the layout of `DomainDetails` (badge, title, description, evidence
 * upload area, requirements list) and `DomainNavigation` (back / checkbox /
 * resume-later / save-and-next).
 */
export const DomainDetailsSkeleton: FC = observer(() => {
  return (
    <Paper
      sx={{
        display: 'flex',
        minHeight: { md: '530px' },
        flexDirection: 'column',
        borderRadius: { xs: 2, sm: 3, md: 4 },
        overflow: 'hidden',
      }}
    >
      {/* Domain Content Area */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          p: { xs: 1.5, sm: 2, md: 3 },
          overflowY: 'auto',
        }}
      >
        {/* Badge Skeleton */}
        <Skeleton
          variant="rounded"
          width={80}
          height={24}
          sx={{ borderRadius: 2, mb: 1.5 }}
        />

        {/* Domain Title */}
        <Skeleton
          variant="text"
          width="40%"
          height={32}
          sx={{ mb: 1 }}
        />

        {/* Domain Description */}
        <Skeleton variant="text" width="100%" height={20} />
        <Skeleton variant="text" width="85%" height={20} sx={{ mb: 2 }} />

        {/* Divider */}
        <Skeleton
          variant="rectangular"
          width="100%"
          height={1}
          sx={{ my: 2 }}
        />

        {/* Evidence Files Section */}
        <Skeleton
          variant="text"
          width={120}
          height={24}
          sx={{ mb: 2 }}
        />

        {/* File Upload Area Skeleton */}
        <Box
          sx={{
            border: '2px dashed',
            borderColor: 'grey.300',
            borderRadius: 2,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            minHeight: 200,
          }}
        >
          {/* Upload Icon */}
          <Skeleton
            variant="circular"
            width={48}
            height={48}
          />
          {/* Upload Text */}
          <Skeleton variant="text" width={200} height={24} />
          <Skeleton variant="text" width={160} height={20} />
          {/* Upload Button */}
          <Skeleton
            variant="rounded"
            width={140}
            height={40}
            sx={{ borderRadius: 2 }}
          />
        </Box>

        {/* Document Requirements */}
        <Box sx={{ mt: 3 }}>
          <Skeleton variant="text" width={180} height={24} sx={{ mb: 1 }} />
          {Array.from({ length: 3 }).map((_, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Skeleton variant="circular" width={8} height={8} />
              <Skeleton variant="text" width={`${60 + index * 10}%`} height={18} />
            </Box>
          ))}
        </Box>
      </Box>

      {/* ============================================ */}
      {/* Navigation Section Skeleton */}
      {/* ============================================ */}
      <Box
        sx={{
          p: { xs: 1.5, sm: 2, md: 3 },
          backgroundColor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          {/* Back Button */}
          <Skeleton
            variant="rounded"
            width={100}
            height={42}
            sx={{ borderRadius: 2 }}
          />

          {/* Right Side Buttons */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            {/* Checkbox */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Skeleton variant="rounded" width={20} height={20} />
              <Skeleton variant="text" width={180} height={20} />
            </Box>

            {/* Resume Later Button */}
            <Skeleton
              variant="rounded"
              width={120}
              height={42}
              sx={{ borderRadius: 2 }}
            />

            {/* Save & Next Button */}
            <Skeleton
              variant="rounded"
              width={140}
              height={42}
              sx={{ borderRadius: 2 }}
            />
          </Box>
        </Box>
      </Box>
    </Paper>
  );
});
